import type {
  BatteryProposalAddressDto,
  BatteryProposalPersonDto,
} from "~/api/dto/battery-proposal.dto"
import useBatteryProposal from "~/api/hooks/useBatteryProposal"
import useUpdateBatteryExistingBattery from "~/api/hooks/useUpdateBatteryExistingBattery"
import { ToggleField } from "~/components/ui-components/v2"

import {
  SetupDetailSection,
  type SetupDetailRow,
} from "../../ProposalSetup/SetupDetailSection"
import { formatPersonPhone, personName } from "../person-display"

/** A field always keeps its label; only the value falls back. */
const NOT_PROVIDED = `Not provided`

const present = (value: string | null | undefined): string =>
  value?.trim() || NOT_PROVIDED

const contactRows = (
  homeowner: BatteryProposalPersonDto | null,
): readonly SetupDetailRow[] => [
  { label: `Full name`, value: present(homeowner && personName(homeowner, ``)) },
  { label: `Email`, value: present(homeowner?.email) },
  {
    label: `Phone`,
    value: present(formatPersonPhone(homeowner?.phone ?? null)),
  },
]

const addressRows = (
  address: BatteryProposalAddressDto | undefined,
): readonly SetupDetailRow[] => [
  { label: `Street`, value: present(address?.street) },
  { label: `City`, value: present(address?.city) },
  { label: `State`, value: present(address?.state) },
  { label: `ZIP`, value: present(address?.postal) },
]

const existingBatteryDescription = (locked: boolean): string =>
  locked
    ? `Locked because a LightReach application exists.`
    : `Ask the homeowner. LightReach Energy Backup is not available for homes that already have a battery.`

const HomeSection = ({ proposalId }: { proposalId: string }): JSX.Element => {
  const { data: proposal } = useBatteryProposal(proposalId)
  const update = useUpdateBatteryExistingBattery()
  const locked = proposal?.existingBatteryLocked ?? false

  return (
    <section aria-label="Home">
      <h3 className="mb-3 text-xs font-semibold tracking-wide text-muted uppercase">
        Home
      </h3>
      <div className="rounded-xl border border-border bg-surface px-4 py-3">
        <ToggleField
          label="Existing battery"
          labelPosition="start"
          description={existingBatteryDescription(locked)}
          error={
            update.isError
              ? `Unable to change Existing battery. Try again.`
              : undefined
          }
          checked={proposal?.hasExistingBattery ?? false}
          disabled={
            !proposal || !proposal.viewerCanUpdate || locked || update.isLoading
          }
          onChange={(hasExistingBattery) => {
            update.mutate({ proposalId, body: { hasExistingBattery } })
          }}
        />
      </div>
    </section>
  )
}

export const SiteStep = ({
  proposalId,
}: {
  proposalId: string
}): JSX.Element => {
  const { data: proposal } = useBatteryProposal(proposalId)

  return (
    <div className="flex flex-col gap-6" data-testid="site-step">
      <p className="text-sm text-muted">
        The contact and service address on file for this home come from the
        project and are read-only here. Record whether the home already has a
        battery.
      </p>
      <SetupDetailSection
        title="Contact"
        rows={contactRows(proposal?.homeowner ?? null)}
      />
      <SetupDetailSection
        title="Service Address"
        rows={addressRows(proposal?.address)}
      />
      <HomeSection proposalId={proposalId} />
    </div>
  )
}
